import { Controller } from "@hotwired/stimulus"
import TomSelect from 'tom-select'

/**
 * Country Select Controller with Tom-select Enhancement
 * 
 * This controller enhances regular select elements with Tom-select for better UX.
 * It's designed to work seamlessly with form search controllers.
 * 
 * IMPORTANT: This controller dispatches native 'change' events that bubble up
 * to parent form controllers. The parent form controller should handle debouncing
 * and search logic.
 */
export default class extends Controller {
  static values = {
    countries: Array,
    selected: String,
    placeholder: String,
    size: String
  }

  connect() {
    this.initializeCountrySelect()
  }

  disconnect() {
    this.destroyTomSelect()
  }

  // FIXED: Add beforeCache for Turbo
  beforeCache() {
    this.destroyTomSelect()
  }

  // FIXED: Centralized destruction method
  destroyTomSelect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }
  }

  // Public API for other controllers
  clear() {
    if (this.tomSelect) {
      this.tomSelect.clear(true) // silent = true to prevent triggering onChange
    }
  }

  setValue(value) {
    if (this.tomSelect) {
      this.tomSelect.setValue(value || '', true) // silent = true to prevent triggering onChange
    }
  }

  // Read-only getter for current value
  getValue() {
    if (this.tomSelect) {
      return this.tomSelect.getValue()
    }
    return this.element.value
  }

  initializeCountrySelect() {
    if (!this.element || this.element.tagName !== 'SELECT') return

    // FIXED: Don't initialize if already exists
    if (this.tomSelect) {
      this.destroyTomSelect()
    }

    const countries = this.countriesValue || []
    const optionsData = {}
    countries.forEach(country => {
      optionsData[country.code] = country
    })

    const config = {
      valueField: 'code',
      labelField: 'name',
      searchField: ['name', 'code'],
      placeholder: this.placeholderValue,
      maxItems: 1,
      // FIXED: This is the key to better UX.
      // It prevents the search input from appearing when an item is selected.
      // The user must first clear the selection to search again.
      allowEmptyOption: true,

      // UX IMPROVEMENT: Prevent search when an item is selected
      closeAfterSelect: true,

      plugins: {
        'clear_button': {
          'title': 'Remove selected option',
        }
      },
      options: Object.values(optionsData),
      // CRITICAL: Only dispatch change events, never call search directly
      onChange: (value) => {
        // Update the original select element
        this.element.value = value || ''

        // Dispatch a native change event that will bubble to the form controller
        // The form controller will handle debouncing and search logic
        this.element.dispatchEvent(new Event('change', {
          bubbles: true,
          cancelable: true
        }))
      },
      onDropdownClose: () => {
        // Blur to prevent focus issues with Turbo navigation
        if (this.tomSelect && this.tomSelect.control_input) {
          this.tomSelect.control_input.blur()
        }
      },
      render: {
        option: (data, escape) => {
          if (!data.flag_class || data.code === '') {
            return `<div class="d-flex align-items-center py-2">
              <span>${escape(data.name)}</span>
            </div>`
          }
          return `<div class="d-flex align-items-center py-2">
            <span class="fi ${escape(data.flag_class)} me-2"></span>
            <span>${escape(data.name)}</span>
            <small class="text-muted ms-auto">${escape(data.code)}</small>
          </div>`
        },
        item: (data, escape) => {
          if (!data.flag_class || data.code === '') {
            return `<span>${escape(data.name)}</span>`
          }
          return `<div class="d-flex align-items-center">
            <span class="fi ${escape(data.flag_class)} me-2"></span>
            <span>${escape(data.name)}</span>
          </div>`
        }
      }
    }

    this.tomSelect = new TomSelect(this.element, config)

    // Set initial value without triggering onChange
    const initialValue = this.selectedValue?.trim()
    if (initialValue && initialValue !== '') {
      this.tomSelect.setValue(initialValue, true) // silent = true
    } else {
      this.tomSelect.clear(true) // silent = true
    }
  }
}